import { useEffect, useRef, useState } from "react";
import {
  X, 
  ArrowDown,
  ArrowRight,
  Check,
} from "lucide-react";

const STEP_DURATION_MS = 10000;

const steps = [
  {
    title: "Start reading and mark words you know",
    header: "Step 1 / 5 · Click any word to relabel them",
    body: "Open a text and click anything that's already familiar. Known words turn to green. Go on, try!",
  },
  {
    title: "Quickly look up words you don't know",
    header: "Step 2 / 5 · Right-click any word to find translations",
    body: "Right-click any new word for a definition, conjugation and an example sentence — no leaving the page!",
  },
  {
    title: "Review the words",
    header: "Step 3 / 5 · Review your newly learned words",
    body: "Optional spaced-repetition reviews on whatever you flagged today and in the past. Voluntarily, takes a few minutes, no streak guilt if you skip it.",
  },
  {
    title: "Keep reading and see your progress",
    header: "Step 4 / 5 · See your progress in other texts",
    body: "As you progress to new texts, words you've learned are already highlighted. You focus entirely on what's new.",
  },
  {
    title: "Ready to proceed? Import any text",
    header: "Step 5 / 5 · Feel free to import any text you want to read",
    body: "Paste a chapter, drag in an .srt file, drop an .epub, or grab song lyrics. Verbatim takes whatever you actually want to read.",
  },
  {
    title: "Convinced? Sign up now",
    body: "Join the waitlist today. The first month is free when Verbatim opens up.",
    cta: true,
  },
];

/* ---------- Per-step preview content ---------- */

const cycle = { new: "known", known: "priority", priority: "ignored", ignored: "new" };

const initialReadingTokens = [
  "Es", "war", "einmal", "eine", "kleine", "süße", "Dirne,", "die", "hatte", "jedermann", "lieb,", "der", "sie", "nur", "ansah,", "am", "allerliebsten", "aber", "ihre", "Großmutter."
].map(t => ({ t, s: "new" }));

const initialHanselTokens = [
  { t: "Vor", s: "known" },
  { t: "einem", s: "known" },
  { t: "großen", s: "known" },
  { t: "Walde", s: "new" },
  { t: "wohnte", s: "new" },
  { t: "ein", s: "known" },
  { t: "armer", s: "new" },
  { t: "Holzhacker", s: "new" },
  { t: "mit", s: "known" },
  { t: "seiner", s: "new" },
  { t: "Frau", s: "new" },
  { t: "und", s: "known" },
  { t: "seinen", s: "new" },
  { t: "zwei", s: "known" },
  { t: "Kindern;", s: "new" },
  { t: "das", s: "known" },
  { t: "Bübchen", s: "new" },
  { t: "hieß", s: "new" },
  { t: "Hänsel", s: "new" },
  { t: "und", s: "known" },
  { t: "das", s: "known" },
  { t: "Mädchen", s: "new" },
  { t: "Gretel.", s: "new" },
];

const hardcodedDict = {
  es: { pos: "pronoun", def: "it" },
  war: { pos: "v · past", def: "was, used to be" },
  einmal: { pos: "adv", def: "once, one time" },
  eine: { pos: "art · fem", def: "a, an (fem.)" },
  kleine: { pos: "adj", def: "little, small" },
  süße: { pos: "adj", def: "sweet, cute" },
  Dirne: { pos: "noun · f", def: "maiden, girl (archaic)" },
  die: { pos: "art · fem", def: "the (fem.)" },
  hatte: { pos: "v · past", def: "had" },
  jedermann: { pos: "pronoun", def: "everyone, everybody" },
  lieb: { pos: "adj", def: "dear, beloved; nice" },
  der: { pos: "art · masc", def: "the (masc.)" },
  sie: { pos: "pronoun", def: "she, her" },
  nur: { pos: "adv", def: "only, just" },
  ansah: { pos: "v · past", def: "looked at, gazed upon" },
  am: { pos: "prep", def: "at the, on the" },
  allerliebsten: { pos: "adj · superlative", def: "dearest, most beloved" },
  aber: { pos: "conj", def: "but, however" },
  ihre: { pos: "pron · poss", def: "her (fem. possessive)" },
  Großmutter: { pos: "noun · f", def: "grandmother" },
};

function PreviewReading({ tokens, onClick, dictIndex, onDictClose, onDictOpen }) {
  return (
    <div className="flex flex-col justify-center h-full p-7 relative overflow-visible">
      <div className="mono-label mb-3">Rotkäppchen · page 1</div>
      <p className="text-lg leading-loose" style={{ fontFamily: "'Cabinet Grotesk', serif" }}>
        {tokens.map((tok, i) => (
          <span key={i} className="inline-block relative">
            <button
              onClick={() => { if (dictIndex === i) onDictClose(); else onClick(i); }}
              onContextMenu={(e) => {
                e.preventDefault();
                const clean = tok.t.replace(/[.,:]/g, "");
                if (hardcodedDict[clean]) {
                  if (dictIndex === i) onDictClose();
                  else onDictOpen(i);
                }
              }}
              className={`rw rw-${tok.s}`}
            >
              {tok.t}
            </button>
            {dictIndex === i && (() => {
              const clean = tok.t.replace(/[.,:]/g, "");
              const entry = hardcodedDict[clean];
              if (!entry) return null;
              return (
                <div
                  className="absolute z-20 card-soft p-3 w-64"
                  style={{
                    top: "100%",
                    left: "50%",
                    marginLeft: "-128px",
                    borderColor: "var(--vb-priority-border)",
                    marginTop: "4px",
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-display font-bold text-sm">{clean}</div>
                      <div className="text-[10px] text-[var(--vb-muted)] italic">{entry.pos}</div>
                    </div>
                    <button className="text-[var(--vb-muted)] hover:text-white" onClick={onDictClose}>
                      <X size={12} />
                    </button>
                  </div>
                  <div className="text-sm mt-1 text-[var(--vb-text)]">{entry.def}</div>
                  <div className="flex gap-2 mt-2">
                    <button className="pill pill-known text-[10px]">
                      <Check size={10} /> Mark known
                    </button>
                    <button className="pill text-[10px]">Save for review</button>
                  </div>
                </div>
              );
            })()}
          </span>
        ))}
      </p>
      <div className="mt-5 flex items-center gap-3 text-xs font-mono text-[var(--vb-muted)]">
        <span className="text-[var(--vb-known)]">●</span> known
        <span className="text-[var(--vb-new)]">●</span> new
        <span className="text-[var(--vb-priority)]">●</span> priority
      </div>
    </div>
  );
}

function PreviewLookup({ tokens, onClick, dictIndex, onDictClose, onDictOpen }) {
  return (
    <div className="flex flex-col justify-center h-full p-7 relative overflow-visible">
      <div className="mono-label mb-3">Rotkäppchen · Brüder Grimm · page 1</div>
      <p className="text-lg leading-loose" style={{ fontFamily: "'Cabinet Grotesk', serif" }}>
        {tokens.map((tok, i) => (
          <span key={i} className="inline-block relative">
            <button
              onClick={() => { if (dictIndex === i) onDictClose(); else onClick(i); }}
              onContextMenu={(e) => {
                e.preventDefault();
                const clean = tok.t.replace(/[.,:]/g, "");
                if (hardcodedDict[clean]) {
                  if (dictIndex === i) onDictClose();
                  else onDictOpen(i);
                }
              }}
              className={`rw rw-${tok.s}`}
            >
              {tok.t}
            </button>
            {dictIndex === i && (() => {
              const clean = tok.t.replace(/[.,:]/g, "");
              const entry = hardcodedDict[clean];
              if (!entry) return null;
              return (
                <div
                  className="absolute z-20 card-soft p-3 w-64"
                  style={{
                    top: "100%",
                    left: "50%",
                    marginLeft: "-128px",
                    borderColor: "var(--vb-priority-border)",
                    marginTop: "4px",
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-display font-bold text-sm">{clean}</div>
                      <div className="text-[10px] text-[var(--vb-muted)] italic">{entry.pos}</div>
                    </div>
                    <button className="text-[var(--vb-muted)] hover:text-white" onClick={onDictClose}>
                      <X size={12} />
                    </button>
                  </div>
                  <div className="text-sm mt-1 text-[var(--vb-text)]">{entry.def}</div>
                  <div className="flex gap-2 mt-2">
                    <button className="pill pill-known text-[10px]">
                      <Check size={10} /> Mark known
                    </button>
                    <button className="pill text-[10px]">Save for review</button>
                  </div>
                </div>
              );
            })()}
          </span>
        ))}
      </p>
      <div className="mt-5 flex items-center gap-3 text-xs font-mono text-[var(--vb-muted)]">
        <span className="text-[var(--vb-known)]">●</span> known
        <span className="text-[var(--vb-new)]">●</span> new
        <span className="text-[var(--vb-priority)]">●</span> priority
      </div>
    </div>
  );
}

function PreviewHansel() {
  const [tokens, setTokens] = useState(initialHanselTokens);

  const onClick = (i) => {
    setTokens((prev) =>
      prev.map((tok, idx) =>
        idx === i && tok.s ? { ...tok, s: cycle[tok.s] } : tok
      )
    );
  };

  return (
    <div className="flex flex-col justify-center h-full p-7">
      <div className="mono-label mb-3">Hänsel und Gretel · Brüder Grimm · page 1</div>
      <p className="text-lg leading-loose" style={{ fontFamily: "'Cabinet Grotesk', serif" }}>
        {tokens.map((tok, i) => (
          <button key={i} onClick={() => onClick(i)} className={`rw rw-${tok.s}`}>
            {tok.t}
          </button>
        ))}
      </p>
      <div className="mt-5 flex items-center gap-3 text-xs font-mono text-[var(--vb-muted)]">
        <span className="text-[var(--vb-known)]">●</span> known
        <span className="text-[var(--vb-new)]">●</span> new
        <span className="text-[var(--vb-priority)]">●</span> priority
      </div>
    </div>
  );
}

function PreviewReview() {
  const cards = [
    { w: "Dirne", d: "girl, maiden", pos: "noun · f" },
    { w: "jedermann", d: "everyone", pos: "pronoun" },
    { w: "Käppchen", d: "little cap", pos: "noun · n" },
  ];
  return (
    <div className="flex flex-col justify-center h-full p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display font-bold text-lg">Review session</div>
        <span className="pill pill-blue text-[10px]">3 of 12</span>
      </div>
      <div className="space-y-3">
        {cards.map((c, i) => (
          <div key={c.w} className={`card-soft p-4 flex items-center justify-between ${i === 0 ? "ring-1 ring-[var(--vb-primary)]" : "opacity-60"}`} style={i === 0 ? {} : { transform: `scale(${1 - i * 0.04})` }}>
            <div>
              <div className="font-display font-bold text-xl">{c.w}</div>
              <div className="text-xs text-[var(--vb-muted)] italic">{c.pos}</div>
            </div>
            {i === 0 && (
              <div className="flex gap-2">
                <button className="w-9 h-9 rounded-md bg-[var(--vb-new-bg)] border border-[var(--vb-new-border)] flex items-center justify-center text-[var(--vb-new)]">✕</button>
                <button className="w-9 h-9 rounded-md bg-[var(--vb-known-bg)] border border-[var(--vb-known-border)] flex items-center justify-center text-[var(--vb-known)]"><Check size={14} /></button>
              </div>
            )}
            {i > 0 && (
              <div className="text-xs text-[var(--vb-muted)] font-mono">
                next
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function PreviewImport() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-6">
      <div className="w-full border-2 border-dashed border-[var(--vb-border-strong)] rounded-xl p-8 bg-[var(--vb-bg)]/40 max-w-md">
        <ArrowDown size={28} className="text-[var(--vb-primary)] mx-auto mb-3" />
        <div className="font-display font-bold text-lg mb-1">
          Drop a file or paste text
        </div>
        <div className="text-xs text-[var(--vb-muted)]">
          .srt · .vtt · .epub · .txt · paste
        </div>
        <div className="flex flex-wrap gap-2 justify-center mt-5">
          {[".srt", ".epub", ".txt"].map((t) => (
            <span key={t} className="pill text-[10px]">
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function PreviewSignup({ onCta }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-6">
      <div className="max-w-md">
        <div className="mono-label mb-3">First month free</div>
        <div className="font-display font-extrabold text-3xl sm:text-4xl leading-tight mb-3">
          Ready when you are.
        </div>
        <p className="text-sm sm:text-base text-[var(--vb-muted)] leading-relaxed">
          Join the waitlist and start with a free first month once Verbatim launches.
        </p>
        <button
          type="button"
          onClick={onCta}
          className="mt-6 btn-primary mx-auto"
        >
          Join the waitlist <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

const previews = [
  PreviewReading,
  PreviewLookup,
  PreviewReview,
  PreviewHansel,
  PreviewImport,
  PreviewSignup,
];

/* ---------- Section ---------- */

export const HowItWorksDemo = ({ onCta }) => {
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tokens, setTokens] = useState(initialReadingTokens);
  const [dictIndex, setDictIndex] = useState(null);
  const startRef = useRef(0);
  const rafRef = useRef(0);

  const onTokenClick = (i) => {
    setTokens((prev) =>
      prev.map((tok, idx) =>
        idx === i && tok.s ? { ...tok, s: cycle[tok.s] } : tok
      )
    );
  };

  useEffect(() => {
    if (paused) {
      cancelAnimationFrame(rafRef.current);
      return;
    }
    startRef.current = performance.now() - (progress / 100) * STEP_DURATION_MS;
    const tick = (now) => {
      const elapsed = now - startRef.current;
      const pct = Math.min((elapsed / STEP_DURATION_MS) * 100, 100);
      setProgress(pct);
      if (pct >= 100) {
        setActive((a) => (a + 1) % steps.length);
        setProgress(0);
        startRef.current = now;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, active]);

  const handleEnter = (i) => {
    setPaused(true);
    if (i !== active) {
      setActive(i);
      setProgress(0);
      setDictIndex(null);
    }
  };

  const handleStepClick = (step) => {
    if (step.cta) {
      onCta?.();
    }
  };

  const handleLeave = () => {
    setPaused(false);
    setProgress(0);
    setDictIndex(null);
  };

  const Preview = previews[active];

  return (
    <section id="flow" data-testid="userflow-section" className="relative py-24 sm:py-28 border-t border-[var(--vb-border)]">
      <div className="container-x">
        <div className="mb-12 reveal">
          <div className="mono-label mb-3">[ 02 · How it works ]</div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            Five steps.{" "}
            <span className="text-[var(--vb-muted)] italic font-light">
              That's the whole loop.
            </span>
          </h2>
          <p className="mt-4 max-w-2xl text-[var(--vb-muted)] text-base">
            Auto-playing walkthrough. Hover any step to pause and jump straight to it. Feel free to interact.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 reveal" onMouseLeave={handleLeave} data-testid="userflow-wrap">
          <div className="lg:col-span-5 lg:h-[430px]">
            <div className="h-full flex flex-col gap-2">
              {steps.map((s, i) => {
                const isActive = i === active;
                const isDone = i < active;
                const isCta = Boolean(s.cta);
                return (
                  <button key={i} type="button" data-testid={`flow-step-${i}`} onMouseEnter={() => handleEnter(i)} onFocus={() => handleEnter(i)} onClick={() => handleStepClick(s)}
                    className={`w-full lg:flex-1 text-left rounded-xl px-4 py-4 lg:py-3 border transition-all ${isCta ? "border-[var(--vb-primary)] bg-[var(--vb-primary-soft)]/10 shadow-[0_0_0_1px_rgba(95,168,255,0.25)] hover:bg-[var(--vb-primary-soft)]/15 hover:shadow-[0_0_0_1px_rgba(95,168,255,0.3)]" : isActive ? "bg-[var(--vb-surface)] border-[var(--vb-primary)]/40 shadow-[0_0_0_1px_rgba(95,168,255,0.15)]" : "bg-transparent border-[var(--vb-border)] hover:border-[var(--vb-border-strong)]"}`}>
                    <div className="flex items-start gap-3">
                      <div className={`shrink-0 w-8 h-8 rounded-md flex items-center justify-center border transition-colors ${isCta ? "bg-[var(--vb-surface-2)] border-[var(--vb-primary)]/35 text-[var(--vb-primary)]" : isActive ? "bg-[var(--vb-primary)] border-[var(--vb-primary)] text-[var(--vb-bg-deeper)]" : isDone ? "bg-[var(--vb-primary-soft)] border-[rgba(95,168,255,0.3)] text-[var(--vb-primary)]" : "bg-[var(--vb-surface-2)] border-[var(--vb-border)] text-[var(--vb-muted)]"}`}>
                        {isCta ? <ArrowRight size={14} strokeWidth={2.5} /> : isDone ? <Check size={14} strokeWidth={3} /> : <span className="text-xs font-mono font-bold">{i + 1}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-display font-bold text-base leading-tight ${isActive ? "text-white" : "text-[var(--vb-text)]"}`}>{s.title}</div>
                        {isActive && (<p className="text-sm text-[var(--vb-muted)] mt-1.5 leading-snug">{s.body}</p>)}
                        <div className="mt-2 h-[2px] bg-[var(--vb-border)] rounded-full overflow-hidden" data-testid={`flow-progress-${i}`}>
                          <div className="h-full bg-[var(--vb-primary)] transition-[width] ease-linear" style={{ width: isActive ? `${progress}%` : isDone ? "100%" : "0%", transitionDuration: paused ? "0ms" : "100ms" }} />
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: preview */}
          <div className="lg:col-span-7">
            <div data-testid="flow-preview" className="card-soft relative h-[400px] sm:h-[430px]">
              <div className="absolute top-0 left-0 right-0 h-10 flex items-center gap-1.5 px-4 border-b border-[var(--vb-border)] bg-[var(--vb-bg-deeper)]/60">
                <span className="w-2 h-2 rounded-full bg-[#ef4444]/60" />
                <span className="w-2 h-2 rounded-full bg-[#fbbf24]/60" />
                <span className="w-2 h-2 rounded-full bg-[#34d399]/60" />
                <span className="mono-label ml-3 !text-[10px]">
                  {steps[active]?.header || "That's all there is to it!"}
                </span>
              </div>
              <div key={active} className="absolute inset-0 pt-10" style={{ animation: "vbFadeIn 500ms cubic-bezier(0.16,1,0.3,1)" }}>
                {active === 1 ? (
                  <PreviewLookup tokens={tokens} onClick={onTokenClick} dictIndex={dictIndex} onDictClose={() => setDictIndex(null)} onDictOpen={(i) => setDictIndex(i)} />
                ) : active === 0 ? (
                  <PreviewReading tokens={tokens} onClick={onTokenClick} dictIndex={dictIndex} onDictClose={() => setDictIndex(null)} onDictOpen={(i) => setDictIndex(i)} />
                ) : (
                  <Preview tokens={tokens} onClick={onTokenClick} onCta={onCta} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes vbFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </section>
  );
};
